import { convertToMoment } from '@/shared/lib';
import dayjs from 'dayjs';
import _ from 'lodash';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import {
  IFormatActivityLogsInput,
  IFormattedActivityLog,
  IFormattedActivityLogItem,
  IPerformer,
} from '../ui/studyDetailsTab/activityLogs/activityLogs.types';

/**
 * Resolves the display name for whoever performed an activity-log action on a study. Maps call-center and operation users to 'Biotricity Support', SYSTEM to 'System', a clinic user to their joined first and last name, and every other case, including a clinic user without any name, to the double-hyphen bullet placeholder.
 *
 * @param performer - Activity-log performer carrying its type and its actionBy user.
 * @returns Display name for the performer, or the placeholder bullet.
 */
export const formatActionBy = (performer: IPerformer) => {
  const { type, actionBy } = performer || {};
  switch (type) {
    case 'CALL_CENTER_USER':
    case 'OPERATION_USER':
      return 'Biotricity Support';
    case 'SYSTEM':
      return 'System';
    case 'CLINIC_USER':
      return actionBy?.firstName || actionBy?.lastName
        ? `${actionBy?.firstName || ''} ${actionBy?.lastName || ''}`
        : DOUBLE_HYPHEN_BULLET;
    default:
      return DOUBLE_HYPHEN_BULLET;
  }
};

/**
 * Groups a study's activity logs into per-day sections for the activity timeline. Deep-clones and orders the logs by trigger time descending, then for each entry derives its study-timezone day key, creates that day bucket with an MM/DD/YYYY display label when missing, and pushes the time, description, sub-descriptions and the performer name resolved from the log's performer.
 *
 * @param info - Logs plus the timezone and study time offset used to bucket them by day.
 * @returns Day sections, each holding its display label, date key and log items.
 */
export const formatActivityLogs = (info: IFormatActivityLogsInput) => {
  const { logs, timezone, studyTimeOffset, startStudyDate, facilityCountry, facilityTimezone } =
    info || {};
  const output = [];
  const clonedInput = _.orderBy(
    _.cloneDeep(logs),
    [(o) => new Date(o.triggerAt).getTime()],
    ['desc'],
  );
  for (let i = 0; i < clonedInput.length; i += 1) {
    const item = clonedInput[i];
    // triggerAt is a valid ISO string from the API, so convertToMoment never returns null here
    const currentStartDate = convertToMoment({
      startStudyDate,
      isoDate: item.triggerAt,
      timezone,
      studyTimeOffset,
      facilityCountry,
      facilityTimezone,
    })!.startOf('day');
    const date = currentStartDate.format('YYYY-M-D');
    const currentMoment = dayjs(item.triggerAt);

    let ele: IFormattedActivityLog | undefined = output.find(
      (x: IFormattedActivityLog) => x.date === date,
    );
    if (!ele) {
      ele = {
        display: currentStartDate.format('MM/DD/YYYY'),
        data: [] as IFormattedActivityLogItem[],
        date,
        time: item.triggerAt,
      };
      output.push(ele);
    }

    ele.data.push({
      time: currentMoment,
      description: item.description,
      subDescriptions: item.subDescriptions || [],
      actionBy: formatActionBy(item.performer),
    });
  }
  return output;
};
